/**
 * Purchase List Page
 *
 * Displays purchase invoices with filtering by status, supplier, and dates.
 * Supports card/table view toggle and kebab menu actions per row.
 *
 * @module pages/purchases/PurchaseList
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Dropdown,
  Collapse,
} from 'react-bootstrap';
import {
  listPurchases,
  cancelPurchase,
  deletePurchase,
  PURCHASE_STATUS,
  PURCHASE_STATUS_LABELS,
  PURCHASE_STATUS_VARIANTS,
  isPurchaseEditable,
} from '../../services/purchaseService';
import { getActiveSuppliers } from '../../services/supplierService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';
import { formatCurrency, formatDate } from '../../utils/formatters';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KebabMenu from '../../components/common/KebabMenu';
import PaginationBar from '../../components/common/PaginationBar';

const PurchaseList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    supplierId: '',
    dateFrom: '',
    dateTo: '',
    page: 0,
    pageSize: 20,
  });

  const [searchInput, setSearchInput] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const searchTimeoutRef = useRef(null);

  // Debounce the search input -> filters.search
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value, page: 0 }));
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Active suppliers for filter dropdown
  const { data: suppliersResponse } = useQuery({
    queryKey: ['active-suppliers'],
    queryFn: getActiveSuppliers,
    staleTime: 1000 * 60 * 5,
  });
  const suppliers = suppliersResponse?.data || [];

  // Fetch purchases
  const {
    data: purchasesResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['purchases', filters],
    queryFn: () => listPurchases(filters),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev) => prev,
  });

  const purchases = purchasesResponse?.data || [];
  const totalCount = purchasesResponse?.count || 0;

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: cancelPurchase,
    onSuccess: (res) => {
      if (res?.error) {
        toast.error('Error al cancelar: ' + (res.error.message || ''));
        return;
      }
      toast.success('Compra cancelada');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      setShowCancelModal(false);
      setSelectedPurchase(null);
    },
    onError: (err) => {
      toast.error('Error al cancelar compra: ' + err.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deletePurchase,
    onSuccess: (res) => {
      if (res?.error) {
        toast.error('Error al eliminar: ' + (res.error.message || ''));
        return;
      }
      toast.success('Compra eliminada');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      setShowDeleteModal(false);
      setSelectedPurchase(null);
    },
    onError: (err) => {
      toast.error('Error al eliminar compra: ' + err.message);
    },
  });

  // OJO: esto fuerza `page: 0` a propósito — cambiar un filtro tiene que
  // volver a la primera página. Por eso NO sirve para paginar: los botones
  // llamaban `handleFilterChange('page', page + 1)` y el `page: 0` de acá
  // pisaba el valor recién puesto, así que "Siguiente" no hacía nada y nunca
  // se salía de la primera página. Paginar tiene su propio handler.
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 0 }));
  };

  const handlePageChange = (nextPage) => {
    setFilters((prev) => ({ ...prev, page: nextPage }));
  };

  const handlePageSizeChange = (nextSize) => {
    setFilters((prev) => ({ ...prev, pageSize: nextSize, page: 0 }));
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.supplierId) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  }, [filters.status, filters.supplierId, filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = activeFiltersCount > 0 || !!filters.search;

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: '',
      supplierId: '',
      dateFrom: '',
      dateTo: '',
      search: '',
      page: 0,
    }));
    setSearchInput('');
  };

  const handleNew = () => navigate('/compras/nueva');
  const handleView = (purchase) => navigate(`/compras/${purchase.id}`);
  const handleEdit = (purchase) => navigate(`/compras/${purchase.id}/editar`);

  const askCancel = (purchase) => {
    setSelectedPurchase(purchase);
    setShowCancelModal(true);
  };

  const askDelete = (purchase) => {
    setSelectedPurchase(purchase);
    setShowDeleteModal(true);
  };

  const renderStatusBadge = (status) => (
    <Badge bg={PURCHASE_STATUS_VARIANTS[status] || 'secondary'}>
      {PURCHASE_STATUS_LABELS[status] || status}
    </Badge>
  );

  const renderKebab = (purchase) => {
    // Flow A: una compra recién creada está en 'invoiced' (el CHECK de la
    // tabla no admite 'pending', que es lo que se comparaba antes — o sea que
    // Editar/Recibir nunca aparecían).
    const isPending = purchase.status === PURCHASE_STATUS.INVOICED;
    // Un borrador de PO (Flujo B) también se edita — ver
    // EDITABLE_PURCHASE_STATUSES. Antes "Editar" sólo aparecía para
    // 'invoiced', así que el borrador recién creado no tenía por dónde
    // corregirse desde el listado.
    const isEditable = isPurchaseEditable(purchase.status);
    return (
      <KebabMenu label={`Acciones de la compra ${purchase.invoice_number || 'sin número'}`}>
          <Dropdown.Item onClick={() => handleView(purchase)}>
            <i className="bi bi-eye me-2"></i>
            Ver Detalle
          </Dropdown.Item>
          {isEditable && (
            <Dropdown.Item onClick={() => handleEdit(purchase)}>
              <i className="bi bi-pencil me-2"></i>
              Editar
            </Dropdown.Item>
          )}
          {purchase.status !== PURCHASE_STATUS.CANCELLED && (
            <Dropdown.Item onClick={() => askCancel(purchase)}>
              <i className="bi bi-x-circle me-2"></i>
              Cancelar Compra
            </Dropdown.Item>
          )}
          {isPending && isAdmin && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => askDelete(purchase)}>
                <i className="bi bi-trash me-2"></i>
                Eliminar
              </Dropdown.Item>
            </>
          )}
      </KebabMenu>
    );
  };

  return (
    <DashboardLayout>
    <Container fluid className="purchase-list">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h2 className="mb-1">
                <i className="bi bi-receipt me-2"></i>
                Compras
              </h2>
              <p className="text-muted mb-0">
                Gestion de facturas de compra y recepcion de inventario
              </p>
            </div>
            <div className="d-flex gap-2">
              {isAdmin && (
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/compras/inbox')}
                  className="d-none d-md-inline-flex align-items-center"
                  data-testid="po-inbox-link"
                >
                  <i className="bi bi-inboxes me-2"></i>
                  Inbox PO
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleNew}
                className="d-none d-md-inline-flex align-items-center"
              >
                <i className="bi bi-plus-lg me-2"></i>
                Nueva Compra
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Search & Filters */}
      <div className="mb-4">
        <div className="d-flex gap-2 align-items-stretch">
          <InputGroup className="flex-grow-1">
            <InputGroup.Text>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="text-muted" />
              ) : (
                <i className="bi bi-search"></i>
              )}
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por numero de factura o ubicacion..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Buscar compras"
            />
          </InputGroup>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button
            variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
            onClick={() => setIsFiltersExpanded((p) => !p)}
            aria-expanded={isFiltersExpanded}
            aria-controls="purchase-advanced-filters"
          >
            <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
            <span className="d-none d-md-inline ms-1">Filtros</span>
            {activeFiltersCount > 0 && (
              <Badge bg="light" text="dark" pill className="ms-1">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>

        <Collapse in={isFiltersExpanded}>
          <div id="purchase-advanced-filters">
            <Card className="mt-3 border-0 shadow-sm">
              <Card.Body>
                <Row className="g-3">
                  <Col xs={12} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">Todos</option>
                      {Object.entries(PURCHASE_STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Proveedor</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.supplierId}
                      onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                    >
                      <option value="">Todos los proveedores</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={6} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Desde</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                  </Col>
                  <Col xs={6} sm={6} lg={3}>
                    <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </Col>
                </Row>
                {hasActiveFilters && (
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      Limpiar filtros
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </Collapse>
      </div>

      {/* Results */}
      <Card className="shadow-sm">
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando compras...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Error al cargar compras: {error.message}
              <Button variant="link" onClick={() => refetch()}>
                Reintentar
              </Button>
            </Alert>
          ) : purchases.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-receipt display-4 text-muted"></i>
              <p className="mt-3 text-muted">
                {hasActiveFilters
                  ? 'No se encontraron compras con los filtros aplicados'
                  : 'No hay compras registradas'}
              </p>
              <Button variant="primary" onClick={handleNew}>
                <i className="bi bi-plus-lg me-2"></i>
                Crear primera compra
              </Button>
            </div>
          ) : isTableView ? (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Factura</th>
                    <th>Fecha</th>
                    <th>Proveedor</th>
                    <th>Ubicacion</th>
                    <th>Estado</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => handleView(p)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="fw-semibold">
                          {p.invoice_number || <span className="text-muted">(Sin numero)</span>}
                        </div>
                        {p.attachment_url && (
                          <small className="text-muted">
                            <i className="bi bi-paperclip me-1"></i>
                            Factura adjunta
                          </small>
                        )}
                      </td>
                      <td>{formatDate(p.invoice_date)}</td>
                      <td>{p.supplier?.name || <span className="text-muted">-</span>}</td>
                      <td>{p.location || <span className="text-muted">-</span>}</td>
                      <td>{renderStatusBadge(p.status)}</td>
                      <td className="text-end fw-semibold">
                        {formatCurrency(p.total, p.currency)}
                        {p.currency === 'USD' && (
                          <small className="d-block text-muted">
                            TC: {p.exchange_rate}
                          </small>
                        )}
                      </td>
                      <td className="text-end">{renderKebab(p)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Row className="g-3">
              {purchases.map((p) => (
                <Col xs={12} sm={6} lg={4} key={p.id}>
                  <Card
                    className="h-100 shadow-sm purchase-card"
                    onClick={() => handleView(p)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            <i className="bi bi-receipt me-2 text-primary"></i>
                            {p.invoice_number || '(Sin numero)'}
                          </h6>
                          <small className="text-muted">{formatDate(p.invoice_date)}</small>
                        </div>
                        {renderKebab(p)}
                      </div>
                      <div className="mb-2">{renderStatusBadge(p.status)}</div>
                      <div className="small text-muted mb-1">
                        <i className="bi bi-building me-2"></i>
                        {p.supplier?.name || 'Sin proveedor'}
                      </div>
                      {p.location && (
                        <div className="small text-muted mb-1">
                          <i className="bi bi-geo-alt me-2"></i>
                          {p.location}
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                        <span className="text-muted small">Total</span>
                        <span className="fw-bold">
                          {formatCurrency(p.total, p.currency)}
                          {p.currency === 'USD' && (
                            <small className="d-block text-end text-muted">
                              TC: {p.exchange_rate}
                            </small>
                          )}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* Paginación — paginador compartido (PaginationBar): muestra
              "Mostrando X-Y de N" y en qué página se está, que es lo que
              faltaba además de los botones que no funcionaban. */}
          {purchases.length > 0 && (
            <PaginationBar
              page={filters.page}
              pageSize={filters.pageSize}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              itemLabel="compras"
            />
          )}
        </Card.Body>
      </Card>

      {/* Mobile FAB */}
      <Button
        variant="primary"
        className="d-md-none position-fixed shadow"
        onClick={handleNew}
        style={{
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          padding: 0,
          zIndex: 1030,
        }}
        aria-label="Nueva Compra"
      >
        <i className="bi bi-plus-lg fs-4"></i>
      </Button>

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
            Confirmar cancelacion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPurchase && (
            <p>
              Estas seguro de cancelar la compra{' '}
              <strong>{selectedPurchase.invoice_number || '(sin numero)'}</strong>?
              Esta accion no se puede deshacer.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Volver
          </Button>
          <Button
            variant="warning"
            onClick={() => selectedPurchase && cancelMutation.mutate(selectedPurchase.id)}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cancelando...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-2"></i>
                Cancelar compra
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-danger me-2"></i>
            Confirmar eliminacion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPurchase && (
            <>
              <p>
                Estas seguro de eliminar la compra{' '}
                <strong>{selectedPurchase.invoice_number || '(sin numero)'}</strong>?
              </p>
              <Alert variant="warning" className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Solo se puede eliminar una compra pendiente sin lotes generados.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => selectedPurchase && deleteMutation.mutate(selectedPurchase.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Eliminando...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </DashboardLayout>
  );
};

export default PurchaseList;
